import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, GraduationCap, HeartHandshake, Library, NotebookPen, CheckCircle2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';

const CRITERIA = [
  {
    icon: GraduationCap,
    title: 'Academic merit',
    text: 'You are enrolled in school or college and can share your course, year of study, student ID, and latest grades.',
  },
  {
    icon: HeartHandshake,
    title: 'Financial need',
    text: 'You can share household income and family details so the scholarship team can review means.',
  },
  {
    icon: Library,
    title: 'Book rental',
    text: 'Scholars keep at least one book rental on mybook.rent every quarter. You may apply before your first rental. Final approval waits until at least one rental is completed.',
  },
  {
    icon: NotebookPen,
    title: 'Essays and review',
    text: 'You write a short essay (200–300 words) on why the scholarship matters, and a review (100–200 words) of a book from mybook.rent.',
  },
];

export default function ScholarshipEligibilityPage() {
  const navigate = useNavigate();
  const [accepted, setAccepted] = useState(false);

  return (
    <div className="min-h-screen" style={{ background: 'var(--background)' }}>
      <Navbar />

      <section
        className="relative overflow-hidden border-b"
        style={{
          borderColor: 'rgba(165, 103, 91, 0.25)',
          background:
            'linear-gradient(135deg, rgba(165,103,91,0.18) 0%, rgba(232,196,140,0.35) 45%, rgba(255,255,255,0.7) 100%)',
        }}
      >
        <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[#A5675B]/15 blur-2xl" />
        <div className="pointer-events-none absolute -bottom-20 left-10 h-48 w-48 rounded-full bg-[#E8C48C]/40 blur-2xl" />
        <div className="relative max-w-4xl mx-auto px-4 py-12 sm:py-16">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 text-xs font-medium tracking-wide uppercase text-[#A5675B] shadow-sm">
            <BookOpen className="h-3.5 w-3.5" />
            mybook.rent scholarships
          </div>
          <h1 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight" style={{ color: 'var(--foreground)' }}>
            Merit-cum-Means Scholarship
          </h1>
          <p className="mt-3 max-w-2xl text-base sm:text-lg text-gray-700 leading-relaxed">
            Support for students who love books and need a hand with studies. We look at both
            academic merit and financial need — then help you keep reading through mybook.rent.
          </p>
        </div>
      </section>

      <main className="max-w-4xl mx-auto px-4 py-10">
        <div className="flex items-end justify-between gap-4 mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Who can apply</h2>
            <p className="mt-1 text-sm text-gray-600">
              Read each point carefully. You will confirm eligibility before the application form opens.
            </p>
          </div>
        </div>

        <ul className="grid sm:grid-cols-2 gap-4">
          {CRITERIA.map((item) => {
            const Icon = item.icon;
            return (
              <li
                key={item.title}
                className="rounded-2xl bg-white/90 p-5 shadow-sm border border-white/60 ring-1 ring-[#A5675B]/10"
              >
                <div
                  className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-white"
                  style={{ backgroundColor: 'var(--primary)' }}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-3 font-semibold text-gray-900">{item.title}</h3>
                <p className="mt-1.5 text-sm text-gray-700 leading-relaxed">{item.text}</p>
              </li>
            );
          })}
        </ul>

        <div className="mt-8 rounded-2xl bg-white p-5 sm:p-6 shadow-sm border border-[#A5675B]/15">
          <div
            role="checkbox"
            aria-checked={accepted}
            tabIndex={0}
            onClick={() => setAccepted((value) => !value)}
            onKeyDown={(event) => {
              if (event.key === ' ' || event.key === 'Enter') {
                event.preventDefault();
                setAccepted((value) => !value);
              }
            }}
            className="flex items-start gap-3 cursor-pointer"
          >
            <Checkbox
              checked={accepted}
              tabIndex={-1}
              className="pointer-events-none mt-0.5"
              data-testid="scholarship-eligibility-confirm"
            />
            <div>
              <p className="text-sm font-medium text-gray-900">
                I have read, understood, and I am eligible as per the criteria.
              </p>
              <p className="mt-1 text-xs text-gray-500">
                Next you will fill personal, educational, and family details, upload your photo, and write two short essays.
              </p>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-3">
            <Button
              disabled={!accepted}
              className="text-white hover:opacity-90 disabled:opacity-40"
              style={{ backgroundColor: 'var(--primary)' }}
              data-testid="scholarship-eligibility-continue"
              onClick={() => navigate('/scholarship/apply')}
            >
              Continue to application
            </Button>
            <Button variant="outline" onClick={() => navigate('/scholarship/status')}>
              Track an application
            </Button>
          </div>

          {accepted && (
            <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-green-700">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Eligibility confirmed — you can continue
            </p>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
